import { useNavigate } from '@tanstack/react-router';
import { useEffect, useId, useState, type JSX, type KeyboardEvent } from 'react';

import type { FilterKey, Security, Ticker, WindowKey } from '@meridian/contracts';
import { SEARCH_LIMIT_DEFAULT } from '@meridian/core';

import { useSearchQuery } from '@/data/queries/stock';
import { useRovingFocus } from '@/lib/a11y/use-roving-focus';
import { useDebouncedValue } from '@/lib/use-debounced-value';
import { useIndexParam } from '@/state/use-index-param';
import { useWindowParam } from '@/state/use-window-param';

import { SearchResults } from './search-results';
import styles from './ticker-search.module.css';

const SEARCH_DEBOUNCE_MS = 200;

function goToTicker(
  navigate: ReturnType<typeof useNavigate>,
  ticker: Ticker,
  win: WindowKey,
  idx: FilterKey,
): void {
  void navigate({
    to: '/stock/$ticker',
    params: { ticker },
    search: { win, idx },
  });
}

function SearchStatus(props: {
  readonly isError: boolean;
  readonly isFetching: boolean;
  readonly hasQuery: boolean;
  readonly hasResults: boolean;
}): JSX.Element | null {
  if (props.isError) {
    return (
      <div className={styles['error']} role="alert">
        Search unavailable
      </div>
    );
  }
  if (props.isFetching && props.hasQuery && !props.hasResults) {
    return (
      <div className={styles['status']} aria-live="polite">
        Searching…
      </div>
    );
  }
  return null;
}

function SearchInput(props: {
  readonly inputId: string;
  readonly listboxId: string;
  readonly q: string;
  readonly expanded: boolean;
  readonly activeOptionId: string | undefined;
  readonly onChange: (value: string) => void;
  readonly onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
}): JSX.Element {
  return (
    <>
      <label className="visually-hidden" htmlFor={props.inputId}>
        Search tickers
      </label>
      <input
        id={props.inputId}
        className={styles['input']}
        type="search"
        role="combobox"
        aria-expanded={props.expanded}
        aria-controls={props.listboxId}
        aria-activedescendant={props.activeOptionId}
        aria-autocomplete="list"
        placeholder="Ticker or name"
        value={props.q}
        onChange={(e) => {
          props.onChange(e.target.value);
        }}
        onKeyDown={props.onKeyDown}
      />
    </>
  );
}

function useTickerSearchModel() {
  const [q, setQ] = useState('');
  const debouncedQ = useDebouncedValue(q, SEARCH_DEBOUNCE_MS);
  const navigate = useNavigate();
  const { window: win } = useWindowParam();
  const { index: idx } = useIndexParam();
  const { data = [], isError, isFetching } = useSearchQuery(debouncedQ, SEARCH_LIMIT_DEFAULT);
  const results = q.trim() === debouncedQ.trim() ? data : [];
  const {
    activeIndex,
    setActiveIndex,
    onKeyDown: onRovingKey,
  } = useRovingFocus({
    count: results.length,
    orientation: 'vertical',
  });
  useEffect(() => {
    setActiveIndex(0);
  }, [debouncedQ, setActiveIndex]);
  const select = (ticker: Ticker): void => {
    setQ('');
    goToTicker(navigate, ticker, win, idx);
  };
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    onRovingKey(e);
    const hit: Security | undefined = results[activeIndex];
    if (e.key === 'Enter' && hit !== undefined) select(hit.ticker);
  };
  return {
    q,
    setQ,
    debouncedQ,
    data: results,
    isError,
    isFetching,
    activeIndex,
    select,
    onKeyDown,
  };
}

export function TickerSearch(): JSX.Element {
  const inputId = useId();
  const listboxId = useId();
  const m = useTickerSearchModel();
  return (
    <div className={styles['root']}>
      <SearchInput
        inputId={inputId}
        listboxId={listboxId}
        q={m.q}
        expanded={m.data.length > 0}
        activeOptionId={
          m.data.length > 0 ? `${listboxId}-option-${String(m.activeIndex)}` : undefined
        }
        onChange={m.setQ}
        onKeyDown={m.onKeyDown}
      />
      {m.isError ? null : (
        <SearchResults
          results={m.data}
          activeIndex={m.activeIndex}
          listboxId={listboxId}
          onSelect={m.select}
        />
      )}
      <SearchStatus
        isError={m.isError}
        isFetching={m.isFetching}
        hasQuery={m.debouncedQ.trim().length > 0}
        hasResults={m.data.length > 0}
      />
    </div>
  );
}
